/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from "react";
import BackButton from "../components/Buttons/BackButton";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Divider, Typography, FormHelperText } from "@mui/material";
import Theme from "../Themes/main.theme";
import { useTranslation } from "react-i18next";
import {
  AppleTitleNamingKeys,
  ILevel3,
  ILocationDetails,
  Title_Naming,
} from "../lib/generalTypes";
import { DropdownOptions } from "../components/form_v2/types";
import ApplicationFormSteps from "../components/FormContainers/applicationLocation/ApplicationFormSteps";
import FormSkeleton from "../components/Skeletons/FormSkeleton";
import { transformFormBodyToFormData } from "../lib/static/locations/formHelpers";
import { MapPoint } from "../components/Maps/types";
import {
  getApplicationLocationDropdownOptions,
  getApplLocationDetails,
  getLocationDetails,
  getOfficeDetails,
  updateApplicationLocationForm,
  uploadApplicationLocationForm,
} from "../lib/static/locations/fetchers";
import { initialValues } from "../lib/static/locations/initialValues";
import { IBasicInfoProperty } from "../components/FormContainers/applicationLocation/Types";
import PreviewContext from "../components/context/PreviewContext";
import { useSnackbar } from "notistack";
import UserService from "../services/UserService";
import { IntExt, Permissions } from "../lib/permissions";
import { Box } from "@mui/system";
import { RequestStatus } from "../components/dictionaries/RequestStatus";
import AsyncBackdropSubmit from "../components/AsyncBackdropSubmit";
import i18n from "../i18n";
import { GetSortOrder } from "../lib/helperFuncs";
import { useFromAccessLogic } from "../hooks/useFromAccessLogic";

const ApplicationFormEdit = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [locationDetails, setLocationDetails] = useState<
    ILocationDetails | undefined
  >(undefined);
  const [level1Options, setlevel1Options] = React.useState<DropdownOptions[]>(
    []
  );
  const [level2Options, setlevel2Options] = React.useState<DropdownOptions[]>(
    []
  );
  const [level3Options, setLevel3Options] = React.useState<ILevel3[]>([]);
  const [regions, setRegions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = React.useState<boolean>(false);
  const { setForm } = useContext(PreviewContext.PreviewProvider.context);
  const { enqueueSnackbar } = useSnackbar();
  const renderFromOmeka = searchParams.get("omeka");
  const is_external = UserService.hasRole([IntExt.External]);

  const { pathname } = useLocation();
  const { availableForms, isLoading: isFormPublishedLoading } =
    useFromAccessLogic(AppleTitleNamingKeys.EditLocation);

  document.title =
    locationDetails?.type_of_application !== Title_Naming.UpdateLocation
      ? locationDetails?.applicant_role === IntExt.External
        ? t("Επεξεργασία Αιτήματος")
        : t("Επεξεργασία Εσωτερικού Αιτήματος Καταχώρισης Τοποθεσίας")
      : is_external
        ? t("Αίτημα Αλλαγής Στοιχείων Τοποθεσίας")
        : t("Εσωτερικό Αίτημα Αλλαγής Στοιχείων Τοποθεσίας");

  const onSubmit = async (
    values: IBasicInfoProperty,
    geolocation: MapPoint
  ) => {
    setIsLoadingSubmit(true);
    const formValues = {
      ...values,
      lat: geolocation?.lat
        ? String(geolocation?.lat)
        : null,
      lon: geolocation?.lng
        ? String(geolocation?.lng)
        : null,
      applicationId: locationDetails?.application_id,
    };

    const formData = transformFormBodyToFormData(formValues);
    let resp;
    if (renderFromOmeka) {
      resp = await uploadApplicationLocationForm(formData);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      resp = await updateApplicationLocationForm(
        formData,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        +locationDetails?.location_id!
      );
    }

    setIsLoadingSubmit(false);

    if (resp?.status === 200 || resp?.status === 201) {
      sessionStorage.setItem("location_id", resp?.data?.location_id.toString());
      sessionStorage.setItem(
        "application_id",
        resp?.data?.application_id.toString()
      );
      resp?.status === 201 &&
        sessionStorage.setItem("mode", "appl_application_edit");
      setForm({
        location_id: resp?.data?.location_id,
        application_id: resp?.data?.application_id,
      });
      enqueueSnackbar(
        t("Το αίτημα καταχώρισης ιδιοκτησίας αποθηκεύτηκε στα Πρόχειρα."),
        { variant: "success" }
      );
      pathname.split("/").filter(Boolean)[0] === "admin"
        ? navigate("/admin/locations/preview")
        : navigate("/preview");
    } else {
      enqueueSnackbar(
        t(
          "Η επεξεργασία αιτήματος ιδιοκτησίας απέτυχε, παρακαλώ προσπαθήστε ξανά."
        ),
        { variant: "error" }
      );
    }
  };

  const getParametrics = async () => {
    const { level1, level2, level3, regionsOptions } =
      await getApplicationLocationDropdownOptions();

    const level1Options_ = level1.map((option1: any) => {
      return {
        key: t(option1.key),
        value: option1.value,
      };
    });

    const level2Options_ = level2
      .map((option2: any) => {
        return {
          key: t(option2.key),
          value: option2.value,
          parent: t(option2.parent),
        };
      })
      .filter((value: any) => value);

    const level3Options_ = level3.map((option3: any) => {
      return {
        ...option3,
        key: t(option3.key),
        parent: t(option3.parent),
      };
    });

    const regions = regionsOptions.map((region: any) => {
      return {
        key: t(region.key),
        value: region.value,
      };
    });

    setlevel1Options(
      i18n.language === "en"
        ? level1Options_.sort(GetSortOrder("key"))
        : level1Options_.sort((a: any, b: any) =>
          a.key.localeCompare(b.key, "el")
        )
    );
    setlevel2Options(
      i18n.language === "en"
        ? level2Options_.sort(GetSortOrder("parent"))
        : level2Options_.sort((a: any, b: any) =>
          a.parent.localeCompare(b.parent, "el")
        )
    );
    setLevel3Options(
      i18n.language === "en"
        ? level3Options_.sort(GetSortOrder("parent"))
        : level3Options_.sort((a: any, b: any) =>
          a.parent.localeCompare(b.parent, "el")
        )
    );
    setRegions(
      i18n.language === "en"
        ? regions.sort(GetSortOrder("key"))
        : regions.sort((a: any, b: any) => a.key.localeCompare(b.key, "el"))
    );
  };

  const getData = async (id: string) => {
    try {
      let detailsLocation;
      if (renderFromOmeka) {
        detailsLocation = await getLocationDetails(id);
      } else {
        detailsLocation = await getApplLocationDetails(id);
      }
      const officeDetails = await getOfficeDetails();

      const applicationBelongsToAnotherOffice =
        detailsLocation.region_id != officeDetails.region_id;
      const userNotResponsibleForTheForm =
        UserService.hasRole([IntExt.Internal]) &&
        UserService.hasRole([
          Permissions.PermissionInternalLocationApplicationModificationRequestCreateMy_org,
        ]) &&
        !UserService.hasRole([
          Permissions.PermissionInternalLocationApplicationModificationRequestCreateAll,
        ]) &&
        applicationBelongsToAnotherOffice;

      if (userNotResponsibleForTheForm) {
        enqueueSnackbar(
          t("Δεν έχετε δικαίωμα επεξεργασίας αυτού του αιτήματος."),
          { variant: "error" }
        );
        applicationBelongsToAnotherOffice &&
          enqueueSnackbar(t("Το αίτημα ανήκει σε άλλη Περιφερειακή Ενότητα"), {
            variant: "error",
          });
        navigate("/admin/locations/dashboard");
      }
      setLocationDetails(detailsLocation);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (is_external && !isFormPublishedLoading) {
      if (!availableForms.is_published) {
        enqueueSnackbar(
          t("Το αίτημα επεξεργασίας δεν είναι διαθέσιμο αυτή τη στιγμή."),
          { variant: "info" }
        );
        navigate("/professionals");
      }
    }
  }, [isFormPublishedLoading]);

  useEffect(() => {
    getParametrics();
    if (id) {
      getData(id);
    }
  }, []);

  return (
    <>
      <AsyncBackdropSubmit isLoading={isLoadingSubmit}>
        {t("Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...")}
      </AsyncBackdropSubmit>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate(-1)} />
      </Box>
      {isLoading || isFormPublishedLoading
        ? (
          <Box mt={4}>
            <FormSkeleton lines={6} />
          </Box>
        )
        : (
          <>
            {is_external &&
              !locationDetails?.is_applicant_user &&
              renderFromOmeka
              ? (
                <Box display='flex' justifyContent='center'>
                  <Typography variant='h3'>
                    {t("Δεν μπορείτε να επεξεργαστείτε το Αίτημα")}
                  </Typography>
                </Box>
              )
              : (
                <>
                  <Typography
                    variant='h2'
                    sx={{ my: 2 }}
                    color={Theme?.labelColor?.main}
                  >
                    {locationDetails?.type_of_application !==
                      Title_Naming.UpdateLocation
                      ? locationDetails?.applicant_role === IntExt.External
                        ? t("Επεξεργασία Αιτήματος")
                        : t("Επεξεργασία Τοποθεσίας")
                      : is_external
                        ? t("Αίτημα Αλλαγής Στοιχείων Τοποθεσίας")
                        : t("Εσωτερικό Αίτημα Αλλαγής Στοιχείων Τοποθεσίας")}
                  </Typography>
                  <Divider
                    sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }}
                  />
                  {locationDetails?.status === RequestStatus.Draft
                    ? (
                      <FormHelperText>
                        <Typography
                          variant='body1'
                          fontStyle='italic'
                          sx={{
                            marginTop: "16px",
                            fontWeight: "bolder",
                          }}
                        >
                          {t(
                            "Πρέπει να συμπληρωθούν όλα τα υποχρεωτικά πεδία, ώστε να κάνετε οριστική υποβολή του αιτήματός σας."
                          )}
                        </Typography>
                      </FormHelperText>
                    )
                    : null}
                  <ApplicationFormSteps
                    edit
                    initialValues={initialValues(locationDetails)}
                    level1Options={level1Options}
                    level2Options={level2Options}
                    level3Options={level3Options}
                    regions={regions}
                    onSubmit={onSubmit}
                    preinserted={locationDetails?.preinserted}
                  />
                </>
              )}
          </>
        )}
    </>
  );
};

export default ApplicationFormEdit;
